import { Ref, ref } from '@vue/composition-api';
import axios, { CancelToken, CancelTokenSource } from 'axios';

export interface ThrottledSearch<A extends unknown[], R> {
	/**
	 * @deprecated Consider using useAsyncStatus wrapped around below "search" function instead
	 */
	isLoading: Ref<boolean>;
	/**
	 * This is the same as the `searchFn` argument below, but without a CancelToken argument
	 */
	search: (...args: A) => Promise<R>;
}

/**
 * Throttle an API that may make multiple requests. Internalizes the cancellation
 * of previous requests when a new one is made.
 *
 * @param searchFn The function that performs the search. Must accept an Axios CancelToken as the first parameter
 * @param shouldShortCircuit A callback, which if it returns true prevents an API call from being made (and similarly prevents cancelling the previous request)
 * @param emptyResultFactory A factory function to create an empty result
 * @returns ThrottledSearch
 */
export const useThrottledApi = <A extends unknown[], R>(
	searchFn: (token: CancelToken, ...args: A) => Promise<R>,
	config: {
		shouldShortCircuit: (...args: A) => boolean;
		emptyResultFactory: (...args: A) => R;
		catchAndReturnEmptyOnCancellation?: boolean;
	},
): ThrottledSearch<A, R> => {
	const isLoading = ref(false);
	let source: CancelTokenSource;
	let result: Promise<R> | undefined;
	const {
		shouldShortCircuit,
		emptyResultFactory,
		catchAndReturnEmptyOnCancellation,
	} = config;
	const search = (...args: A): Promise<R> => {
		if (shouldShortCircuit(...args)) {
			return Promise.resolve(emptyResultFactory(...args));
		}
		isLoading.value = true;
		if (source && result) {
			source.cancel();
		}
		source = axios.CancelToken.source();
		return searchFn(source.token, ...args)
			.then((val) => {
				isLoading.value = false;
				return val;
			})
			.catch((thrown) => {
				if (axios.isCancel(thrown) && catchAndReturnEmptyOnCancellation) {
					return emptyResultFactory(...args);
				}
				return Promise.reject(thrown);
			});
	};
	return { search, isLoading };
};
